import { AfterViewInit, Directive, ElementRef, HostListener, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[hmaxFloat]',
  standalone: true,
})
export class FloatDirective implements OnChanges, AfterViewInit {
  private _element = inject(ElementRef);
  private _ngControl = inject(NgControl);

  @Input() hmaxFloat = false;
  @Input() decimalQty = 2;
  @Input() emitEvent = false;
  @Input() allowNegative = false;

  ngAfterViewInit(): void {
    if (this.hmaxFloat) {
      this.reformatOnModeChange();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('hmaxFloat' in changes && !changes['hmaxFloat'].firstChange) {
      this.reformatOnModeChange();
    } else if (
      ('decimalQty' in changes && !changes['decimalQty'].firstChange) ||
      ('allowNegative' in changes && !changes['allowNegative'].firstChange)
    ) {
      this.formatValue();
    }
  }

  private reformatOnModeChange(): void {
    const input = this._element.nativeElement as HTMLInputElement;
    const numericValue = parseFloat(input.value);

    if (isNaN(numericValue)) {
      return;
    }

    const formatted = this.hmaxFloat ? numericValue.toFixed(this.decimalQty) : String(Math.round(numericValue));

    this.updateValue(formatted, input);
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const input = this._element.nativeElement as HTMLInputElement;
    const key = event.key;

    if (this.isControlKey(key)) {
      return;
    }

    if (key === '-') {
      this.handleNegativeSign(event, input);
      return;
    }

    if (!this.isKeyAllowed(key, input.value)) {
      event.preventDefault();
    }
  }

  private isControlKey(key: string): boolean {
    const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab', 'Home', 'End', 'Enter'];
    return allowedKeys.includes(key);
  }

  private handleNegativeSign(event: KeyboardEvent, input: HTMLInputElement): void {
    event.preventDefault();
    if (!this.allowNegative) {
      return;
    }

    const currentValue = input.value || this._ngControl?.value?.toString() || '';

    const newValue = currentValue.startsWith('-') ? currentValue.slice(1) : '-' + currentValue;
    this.updateValue(newValue, input);
  }

  private isKeyAllowed(key: string, currentValue: string): boolean {
    const isNumber = key >= '0' && key <= '9';
    const isDecimalSeparator = this.hmaxFloat && (key === '.' || key === ',');

    if (isDecimalSeparator) {
      return !currentValue.includes('.') && !currentValue.includes(',');
    }

    return isNumber;
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text') ?? '';
    const validNumberRegex = this.hmaxFloat ? /^-?\d+([.,]\d*)?$/ : /^-?\d+$/;

    if (!validNumberRegex.test(pasted)) {
      event.preventDefault();
    }
  }

  @HostListener('input', ['$event'])
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onInput(event: Event): void {
    this.formatValue();
  }

  /**
   * Método auxiliar para atualizar tanto o DOM quanto o FormControl
   */
  private updateValue(newValue: string, input: HTMLInputElement): void {
    if (this._ngControl) {
      this._ngControl.control?.setValue(newValue, { emitEvent: this.emitEvent });
      if (input.value !== newValue) {
        input.value = newValue;
      }
    } else {
      input.value = newValue;
    }
  }

  private formatValue(): void {
    const input = this._element.nativeElement as HTMLInputElement;
    const originalValue = this.getInitialValue(input);
    const isNegative = this.allowNegative && originalValue.startsWith('-');
    const digits = input.value.replace(/[^0-9]/g, '');

    if (!digits) {
      this.handleEmptyState(isNegative, input);
      return;
    }

    let formattedValue = this.hmaxFloat ? this.formatAsFloat(digits) : digits;

    if (isNegative && parseFloat(formattedValue) !== 0) {
      formattedValue = `-${formattedValue}`;
    }

    this.updateValue(formattedValue, input);
  }

  private getInitialValue(input: HTMLInputElement): string {
    const value = input.value;
    const controlValue = this._ngControl?.value;
    return !value && controlValue === '-' ? '-' : value;
  }

  private handleEmptyState(isNegative: boolean, input: HTMLInputElement): void {
    const newValue = isNegative ? '-' : '';

    if (input.value !== newValue) {
      input.value = newValue;
    }

    if (this._ngControl && this._ngControl.value !== newValue) {
      this._ngControl.control?.setValue(newValue, { emitEvent: this.emitEvent });
    }
  }

  private formatAsFloat(digits: string): string {
    const length = digits.length;
    const integerPart = digits.slice(0, length - this.decimalQty) || '0';
    const decimalPart = digits.slice(length - this.decimalQty).padStart(this.decimalQty, '0');

    return parseFloat(`${integerPart}.${decimalPart}`).toFixed(this.decimalQty);
  }
}
